import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { SliceType } from '~/enums/SliceType';

import type {
  AiWorkflowBuild,
  AiWorkflowBuildsState,
  AiWorkflowBuildStatus,
} from './types';

export const initialState: AiWorkflowBuildsState = {
  builds: [],
};

type UpdateBuildPayload = {
  build_id: string;
  status: AiWorkflowBuildStatus;
  workflow_id?: string | null;
  error?: string | null;
};

const TERMINAL_STATUSES: ReadonlySet<AiWorkflowBuildStatus> = new Set([
  'failed',
  'ready',
]);

const getUpdatedAtMs = (build: AiWorkflowBuild): number => {
  const timestamp = Date.parse(build.updated_at);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getNewestBuild = (
  localBuild: AiWorkflowBuild | undefined,
  serverBuild: AiWorkflowBuild,
): AiWorkflowBuild => {
  if (!localBuild) return serverBuild;
  if (getUpdatedAtMs(localBuild) > getUpdatedAtMs(serverBuild)) {
    return localBuild;
  }
  const unreadPinned = localBuild.unread_pinned ?? false;
  return {
    ...serverBuild,
    started_toast_id:
      serverBuild.started_toast_id ?? localBuild.started_toast_id,
    title: serverBuild.title ?? localBuild.title,
    unread_pinned: unreadPinned,
    // Read state is sticky in both directions: a local mark-as-read must
    // survive a hydrate that races ahead of the server persisting read_at, and
    // a terminal transition that resurfaced the build as unread must not be
    // undone by the stale read_at the server still reports for it.
    is_read: unreadPinned ? false : serverBuild.is_read || localBuild.is_read,
  };
};

const aiWorkflowBuilds = createSlice({
  name: SliceType.AiWorkflowBuilds,
  initialState,
  selectors: {
    selectBuilds: (state) => state.builds,
    selectBuildCount: (state) => state.builds.length,
  },
  reducers: {
    hydrateBuilds: (state, action: PayloadAction<AiWorkflowBuild[]>): void => {
      const localBuildsById = new Map(
        state.builds.map((build) => [build.build_id, build]),
      );
      const serverBuildIds = new Set(
        action.payload.map((build) => build.build_id),
      );

      const localOnlyBuilds = state.builds.filter(
        (build) => !serverBuildIds.has(build.build_id),
      );
      const hydratedBuilds = action.payload.map((serverBuild) =>
        getNewestBuild(localBuildsById.get(serverBuild.build_id), serverBuild),
      );

      state.builds = [...localOnlyBuilds, ...hydratedBuilds];
    },
    addBuild: (state, action: PayloadAction<AiWorkflowBuild>): void => {
      state.builds = [
        action.payload,
        ...state.builds.filter(
          (build) => build.build_id !== action.payload.build_id,
        ),
      ];
    },
    updateBuild: (state, action: PayloadAction<UpdateBuildPayload>): void => {
      const build = state.builds.find(
        (item) => item.build_id === action.payload.build_id,
      );
      if (!build) return;

      const nextStatus = action.payload.status;
      // Reaching a terminal outcome (ready/failed) is new information the person
      // hasn't seen: resurface it as unread even if they read the in-progress
      // notification. Pin it so a later hydrate carrying the stale server
      // read_at can't quietly re-mark it read.
      if (
        nextStatus !== build.status &&
        TERMINAL_STATUSES.has(nextStatus) &&
        build.is_read
      ) {
        build.is_read = false;
        build.unread_pinned = true;
      }

      build.status = nextStatus;
      build.workflow_id = action.payload.workflow_id ?? build.workflow_id;
      build.error = action.payload.error ?? build.error;
      build.updated_at = new Date().toISOString();
    },
    markBuildRead: (state, action: PayloadAction<string>): void => {
      const build = state.builds.find(
        (item) => item.build_id === action.payload,
      );
      if (build) {
        build.is_read = true;
        build.unread_pinned = false;
      }
    },
    dismissBuild: (state, action: PayloadAction<string>): void => {
      state.builds = state.builds.filter(
        (build) => build.build_id !== action.payload,
      );
    },
  },
});

export default aiWorkflowBuilds;
